import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  ChangeEvent,
} from 'react';
import ReactHtmlParser from 'react-html-parser';

import {
  FormWrapper,
  Form,
  InputField,
  ErrorMsg,
  BladeSubtitle,
} from './styles';

interface FormField {
  displayLabel: string;
  placeholderLabel: string;
  errorMessage: string;
}

interface Props {
  formData: Record<string, FormField>;
  deliverFormField: (field: string, value: string) => void;
  personalDetails: Record<string, string>;
  hasErrorPersonalDetails: boolean;
  checkErrorTrigger?: boolean;
}

const ReserveForm = ({
  formData,
  deliverFormField,
  personalDetails,
  hasErrorPersonalDetails,
  checkErrorTrigger = true,
}: Props) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);

  const [hasErrorFirstNameInvalid, setHasErrorFirstNameInvalid] = useState(false);
  const [hasErrorLastNameInvalid, setHasErrorLastNameInvalid] = useState(false);
  const [hasErrorEmailInvalid, setHasErrorEmailInvalid] = useState(false);
  const [hasErrorPhoneInvalid, setHasErrorPhoneInvalid] = useState(false);

  const errorField = 'errorField';
  const validField = 'validField';
  const [statusFirstName, setStatusFirstName] = useState('');
  const [statusLastName, setStatusLastName] = useState('');
  const [statusEmail, setStatusEmail] = useState('');
  const [statusPhone, setStatusPhone] = useState('');

  useEffect(() => {
    if (personalDetails.lastName) setLastName(personalDetails.lastName);
    if (personalDetails.firstName) setFirstName(personalDetails.firstName);
    if (personalDetails.email) setEmail(personalDetails.email);
    if (personalDetails.phone) setPhone(personalDetails.phone);
  }, [
    personalDetails.email,
    personalDetails.firstName,
    personalDetails.lastName,
    personalDetails.phone,
  ]);

  const handleChangeFirstName = (e: ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
    if (document.activeElement !== e.target) handleBlurFirstName(e.target.value);
  };
  const handleChangeLastName = (e: ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
    if (document.activeElement !== e.target) handleBlurLastName(e.target.value);
  };
  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (document.activeElement !== e.target) handleBlurEmail(e.target.value);
  };
  const handleChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
    if (document.activeElement !== e.target) handleBlurPhone(e.target.value);
  };

  const handleBlurFirstName = useCallback((defaultValue?: string) => {
    const val = defaultValue ?? firstName;
    setHasErrorFirstNameInvalid(false);
    if (!checkNameInvalid(val)) {
      setHasErrorFirstNameInvalid(true);
      setStatusFirstName(errorField);
    } else {
      setStatusFirstName(validField);
      deliverFormField('firstName', val);
    }
  }, [deliverFormField, firstName]);

  const handleBlurLastName = useCallback((defaultValue?: string) => {
    const val = defaultValue ?? lastName;
    setHasErrorLastNameInvalid(false);
    if (!checkNameInvalid(val)) {
      setHasErrorLastNameInvalid(true);
      setStatusLastName(errorField);
    } else {
      setStatusLastName(validField);
      deliverFormField('lastName', val);
    }
  }, [deliverFormField, lastName]);

  const handleBlurEmail = useCallback((defaultValue?: string) => {
    const val = defaultValue ?? email;
    setHasErrorEmailInvalid(false);
    if (!checkEmailInvalid(val)) {
      setHasErrorEmailInvalid(true);
      setStatusEmail(errorField);
    } else {
      setStatusEmail(validField);
      deliverFormField('email', val);
    }
  }, [deliverFormField, email]);

  const handleBlurPhone = useCallback((defaultValue?: string) => {
    const val = defaultValue ?? phone;
    const sanitized = val.replace(/\D/g, '');
    setHasErrorPhoneInvalid(false);
    if (!checkPhoneInvalid(val)) {
      setHasErrorPhoneInvalid(true);
      setStatusPhone(errorField);
    } else {
      setStatusPhone(validField);
      deliverFormField('phone', sanitized);
    }
  }, [deliverFormField, phone]);

  useEffect(() => {
    if (hasErrorFirstNameInvalid) {
      firstNameRef.current?.focus();
    } else if (hasErrorLastNameInvalid) {
      lastNameRef.current?.focus();
    } else if (hasErrorEmailInvalid) {
      emailRef.current?.focus();
    } else if (hasErrorPhoneInvalid) {
      phoneRef.current?.focus();
    }
  }, [
    checkErrorTrigger,
    hasErrorEmailInvalid,
    hasErrorFirstNameInvalid,
    hasErrorLastNameInvalid,
    hasErrorPhoneInvalid,
  ]);

  const checkNameInvalid = (name: string): boolean => {
    if (name === '') return false;
    const pattern = new RegExp(/[^A-z-'\s]/, 'g');
    return !pattern.test(name);
  };

  const checkEmailInvalid = (emailAddress: string): boolean => {
    if (emailAddress === '') return false;
    const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return pattern.test(emailAddress);
  };

  const checkPhoneInvalid = (phoneNumber: string): boolean => {
    if (phoneNumber === '') return false;
    const testNumber = phoneNumber.replace(/\D/g, '');
    const formatNumber = /^\d{10}$/;
    const pattern1 = /^[1-9]\d{9}$/;
    const pattern2 = /^\d11/;
    const pattern3 = /(\d)\1{9}/;
    const pattern4 = /(\d)\1{6}$/;
    const sequential = [
      /1234567890/, /2345678901/, /3456789012/, /4567890123/, /5678901234/,
      /6789012345/, /7890123456/, /8901234567/, /9012345678/,
    ];

    return (
      formatNumber.test(testNumber)
      && pattern1.test(testNumber)
      && !pattern2.test(testNumber)
      && !pattern3.test(testNumber)
      && !pattern4.test(testNumber)
      && !sequential.some((pattern) => pattern.test(testNumber))
    );
  };

  useEffect(() => {
    if (hasErrorPersonalDetails === true) {
      handleBlurFirstName();
      handleBlurLastName();
      handleBlurEmail();
      handleBlurPhone();
    }
  }, [
    handleBlurEmail,
    handleBlurFirstName,
    handleBlurLastName,
    handleBlurPhone,
    hasErrorPersonalDetails,
  ]);

  return (
    <FormWrapper>
      <BladeSubtitle>
        {ReactHtmlParser(formData['7rBYPQppwxiGa1byQSEWOj'].displayLabel)}
      </BladeSubtitle>
      <Form>
        {/* FIRST NAME */}
        <fieldset className="half">
          <label htmlFor="firstName">
            {ReactHtmlParser(formData['3s7nTpvPsuR5vnlzyn9eew'].displayLabel)}
          </label>
          <InputField
            onChange={handleChangeFirstName}
            onBlur={() => handleBlurFirstName()}
            type="text"
            name="firstName"
            id="firstName"
            required
            maxLength={25}
            minLength={2}
            placeholder={formData['3s7nTpvPsuR5vnlzyn9eew'].placeholderLabel}
            status={statusFirstName}
            value={firstName}
            ref={firstNameRef}
            data-testid="reserve-form-firstname"
          />
          {hasErrorFirstNameInvalid && (
            <ErrorMsg role="alert" tabIndex={-1}>
              &#x2718;
              {' '}
              {formData['3s7nTpvPsuR5vnlzyn9eew'].errorMessage}
            </ErrorMsg>
          )}
        </fieldset>

        {/* LAST NAME */}
        <fieldset className="half">
          <label htmlFor="lastName">
            {ReactHtmlParser(formData['7vgMYuHzAzbM1ZNc3WXAde'].displayLabel)}
          </label>
          <InputField
            onChange={(e) => handleChangeLastName(e)}
            onBlur={() => handleBlurLastName()}
            type="input"
            name="lastName"
            id="lastName"
            required
            maxLength={25}
            minLength={2}
            placeholder={ReactHtmlParser(
              formData['7vgMYuHzAzbM1ZNc3WXAde'].placeholderLabel,
            )}
            status={statusLastName}
            value={lastName}
            ref={lastNameRef}
            data-testid="reserve-form-lastname"
          />
          {hasErrorLastNameInvalid && (
            <ErrorMsg role="alert" tabIndex={-1}>
              &#x2718;
              {' '}
              {ReactHtmlParser(formData['7vgMYuHzAzbM1ZNc3WXAde'].errorMessage)}
            </ErrorMsg>
          )}
        </fieldset>

        {/* EMAIL */}
        <fieldset>
          <label htmlFor="email">
            {ReactHtmlParser(formData['4c8nsZU528mPagy0mRee7w'].displayLabel)}
          </label>
          <InputField
            onChange={(e) => handleChangeEmail(e)}
            onBlur={() => handleBlurEmail()}
            type="email"
            inputMode="email"
            name="email"
            id="email"
            required
            maxLength={320}
            minLength={3}
            placeholder={ReactHtmlParser(
              formData['4c8nsZU528mPagy0mRee7w'].placeholderLabel,
            )}
            status={statusEmail}
            value={email}
            ref={emailRef}
            data-testid="reserve-form-email"
          />
          {hasErrorEmailInvalid && (
            <ErrorMsg role="alert" tabIndex={-1}>
              &#x2718;
              {' '}
              {ReactHtmlParser(formData['4c8nsZU528mPagy0mRee7w'].errorMessage)}
            </ErrorMsg>
          )}
        </fieldset>

        {/* PHONE NUMBER */}
        <fieldset id="phoneField">
          <label htmlFor="phone">
            {ReactHtmlParser(formData['284rkgAywyML4cW5AIPWCg'].displayLabel)}
          </label>
          <InputField
            onChange={(e) => handleChangePhone(e)}
            onBlur={() => handleBlurPhone()}
            type="tel"
            inputMode="tel"
            name="phone"
            id="phone"
            required
            maxLength={20}
            minLength={10}
            placeholder={ReactHtmlParser(
              formData['284rkgAywyML4cW5AIPWCg'].placeholderLabel,
            )}
            status={statusPhone}
            value={phone}
            ref={phoneRef}
            data-testid="reserve-form-phone"
          />
          {hasErrorPhoneInvalid && (
            <ErrorMsg role="alert" tabIndex={-1}>
              &#x2718;
              {' '}
              {ReactHtmlParser(formData['284rkgAywyML4cW5AIPWCg'].errorMessage)}
            </ErrorMsg>
          )}
        </fieldset>
      </Form>
    </FormWrapper>
  );
};

export default ReserveForm;
